import React, { useState, useEffect } from "react";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from "jwt-decode";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Auth.css";
import { useNavigate } from "react-router-dom";
import Logo from "../Assets/BooksCompl.png";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add("login-body");
    return () => {
      document.body.classList.remove("login-body");
    };
  }, []);

  const handleOAuthLogin = async (_provider, token) => {
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed.");
      }

      const data = await response.json();
      localStorage.setItem("token", data.token);
      setMessage("Login successful! Redirecting...");
      
      // Redirect to appropriate page based on unlocked services
      setTimeout(() => {
        // Check if all services are unlocked - if so, prioritize Tax Filing
        if (data.user.isUSAEnabled && data.user.isTaxFilingEnabled && 
            data.user.isBookkeepingEnabled && data.user.isCFOEnabled) {
          navigate("/tax-filing");
        } else if (data.user.isUSAEnabled) {
          navigate("/usa");
        } else if (data.user.isTaxFilingEnabled) {
          navigate("/tax-filing");
        } else if (data.user.isBookkeepingEnabled) {
          navigate("/bookkeeping");
        } else if (data.user.isCFOEnabled) {
          navigate("/cfo");
        } else {
          // No services unlocked, redirect to pricing
          navigate("/pricing");
        }
      }, 1000);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed.");
      }

      const data = await response.json();
      localStorage.setItem("token", data.token);
      localStorage.setItem("userId", data.user._id);
      setMessage("Login successful! Redirecting...");
      
      // Redirect to appropriate page based on unlocked services
      setTimeout(() => {
        // Check if all services are unlocked - if so, prioritize Tax Filing
        if (data.user.isUSAEnabled && data.user.isTaxFilingEnabled && 
            data.user.isBookkeepingEnabled && data.user.isCFOEnabled) {
          navigate("/tax-filing");
        } else if (data.user.isUSAEnabled) {
          navigate("/usa");
        } else if (data.user.isTaxFilingEnabled) {
          navigate("/tax-filing");
        } else if (data.user.isBookkeepingEnabled) {
          navigate("/bookkeeping");
        } else if (data.user.isCFOEnabled) {
          navigate("/cfo");
        } else {
          // No services unlocked, redirect to pricing
          navigate("/pricing");
        }
      }, 1000);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId="383385845506-jknb7cqrrmbmhnki2vbdkindqdpmpce8.apps.googleusercontent.com">
      <div className="login-page">
        <div className="main-logo">
          <img src={Logo} alt="Logo" className="logo-img" />
        </div>
        <div className="login-container">
          <div className="login-card">
            <h2 className="login-title">Login to Your Account</h2>
            <form onSubmit={handleEmailLogin}>
              <div className="form-group position-relative mb-3">
                <FaEnvelope className="input-icon" />
                <input
                  type="email"
                  className="form-control"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="form-group position-relative mb-2">
                <FaLock className="input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
              <div className="forgot-password text-end mb-3">
                <a href="/forgetpassword">Forgot Password?</a>
              </div>
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Logging in...
                  </>
                ) : "Login"}
              </button>
            </form>
            <div className="oauth-buttons mt-4">
              <GoogleLogin
                onSuccess={(credentialResponse) => {
                  try {
                    const decoded = jwtDecode(credentialResponse.credential);
                    console.log("Decoded JWT:", decoded);
                    handleOAuthLogin("Google", credentialResponse.credential);
                  } catch (error) {
                    console.error("Error decoding token:", error);
                    setMessage("Failed to decode token. Please try again.");
                  }
                }}
                onError={() => {
                  console.log("Google Login Failed");
                  setMessage("Google login failed. Please try again.");
                }}
              />
            </div>
           
            <p className="mt-3 text-center">
              Don't have an account?
              <a href="/signup" style={{ textDecoration: "none" }}> Sign Up</a>
            </p>
            {message && (
              <div
                className={`alert mt-3 ${message.includes("successful") ? "alert-success" : "alert-danger"
                  }`}
              >
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    </GoogleOAuthProvider> 
  );
};
export default LoginPage;